import { esc, initials } from './format.ts';

/** The agency's carriers: their names and what each one lets the agency do. */

/** The agency's carriers as their packs name them (GET /v1/carriers, loaded at sign-in). */
export let carrierNames: Record<string, string> = {};
export function setCarrierNames(names: Record<string, string>) {
  carrierNames = names;
}
export const carrierName = (id: string) => carrierNames[id] ?? id;

/**
 * The carriers' plans as their catalogs name them (#307, GET /v1/catalog/plan-names, loaded at
 * sign-in): carrier → product line → plan code → name (the agency's commercial name where it set
 * one). A plan the catalog does not have shows its code, as the carrier sends it.
 */
export type PlanNames = Record<string, Record<string, Record<string, string>>>;
let planNames: PlanNames = {};
export function setPlanNames(names: PlanNames) {
  planNames = names;
}
export function planName(carrier: string, code: string, productLine?: string): string {
  const lines = planNames[carrier] ?? {};
  if (productLine) return lines[productLine]?.[code] ?? code;
  for (const byCode of Object.values(lines)) if (byCode[code]) return byCode[code];
  return code;
}

/**
 * Each carrier's status in the platform and whether the agency quotes and issues new with it now
 * (ADR-0027, loaded with the names). A carrier not listed is offered: the API decides anyway.
 */
export type CarrierOffer = { platformStatus: 'ACTIVE' | 'TESTING' | 'OFF'; offered: boolean };
export let carrierOffers: Record<string, CarrierOffer> = {};
export function setCarrierOffers(offers: Record<string, CarrierOffer>) {
  carrierOffers = offers;
}
/** Whether a saved quote of this carrier can still be issued. */
export const offeredNow = (id: string) => carrierOffers[id]?.offered ?? true;
/** A carrier in testing the agency pilots: «En pruebas». */
export const inTesting = (id: string) => carrierOffers[id]?.platformStatus === 'TESTING';
export const carrierCell = (id: string, name = carrierName(id)) =>
  `<span class="carrier-cell"><span class="monogram" aria-hidden="true">${esc(initials(name))}</span>${esc(name)}</span>`;

export type CarrierView = {
  id: string;
  name: string;
  collectedBy: 'AGENCY' | 'CARRIER' | null;
  endorse: Record<string, string[]> | null;
  renew: boolean;
  /** `byDeposit`: the carrier takes no remittances through a system; the deposit is the remittance. */
  remittance: { basis: 'GROSS' | 'NET_OF_COMMISSION'; byDeposit?: boolean } | null;
  statement: boolean;
  /** The documents it gives for a policy: POLICY, CERTIFICATE, QUOTE. */
  documents?: string[];
  /** Its status in the platform and whether the agency quotes and issues new with it (#283). */
  platformStatus?: CarrierOffer['platformStatus'];
  offered?: boolean;
};
